import { useMemo } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { queryClient } from '../app/query-client';
import { Route42Logo } from '../components/brand/Route42Logo';
import { useAuth } from './AuthProvider';
import {
  hasUnavailableOrganizationAccess,
  isMembershipSuspended,
  isOrganizationSuspended,
} from './organization-access';
import type { Route42Membership } from './types';

function membershipFromLocation(
  memberships: readonly Route42Membership[],
  search: string,
): Route42Membership | null {
  const requestedCustomerId = new URLSearchParams(search).get('organization');
  if (requestedCustomerId) {
    const requested = memberships.find((membership) => membership.customer_id === requestedCustomerId);
    if (requested) return requested;
  }
  return memberships.find(hasUnavailableOrganizationAccess) ?? null;
}

export function OrganizationUnavailablePage() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const memberships = auth.profile?.memberships ?? [];
  const membership = useMemo(
    () => membershipFromLocation(memberships, location.search),
    [location.search, memberships],
  );
  const otherOrganizations = memberships.filter(
    (candidate) => candidate.access_available && candidate.customer_id !== membership?.customer_id,
  );

  if (membership?.access_available) {
    return <Navigate to="/start" replace />;
  }

  async function handleSignOut() {
    queryClient.clear();
    await auth.signOut();
    navigate('/sign-in', { replace: true });
  }

  if (!membership) {
    return (
      <main className="r42-organization-unavailable-page">
        <section className="r42-organization-unavailable-card" role="status">
          <Route42Logo variant="lockup" />
          <span className="r42-eyebrow">Organization access</span>
          <h1>No active Route42 organization is available</h1>
          <p>Your Route42 account is signed in, but it does not currently have an organization workspace that can be opened.</p>
          <div className="r42-organization-unavailable-guidance">
            <strong>What to do next</strong>
            <p>Contact your Route42 organization administrator or Route42 support to confirm your organization access.</p>
          </div>
          <div className="r42-organization-unavailable-actions">
            <button className="btn btn-outline-secondary" type="button" onClick={() => void handleSignOut()}>Sign out</button>
          </div>
        </section>
      </main>
    );
  }

  const organizationName = membership.customer_name ?? 'This Route42 organization';
  const organizationSuspended = isOrganizationSuspended(membership);
  const membershipSuspended = isMembershipSuspended(membership);
  const title = organizationSuspended
    ? `${organizationName} is suspended`
    : membershipSuspended
      ? `Your access to ${organizationName} is suspended`
      : `${organizationName} is unavailable`;
  const explanation = organizationSuspended
    ? `Access to ${organizationName} is temporarily unavailable because the organization is suspended. Your Route42 account is still active, but this organization's machines, conversations, investigations, documents, and Machine Memory cannot be accessed until the organization is reactivated.`
    : membershipSuspended
      ? `Your Route42 account is still active, but your membership in ${organizationName} is suspended. You cannot enter this organization's workspace until your access is reactivated.`
      : `${organizationName} is not currently available to this Route42 account.`;
  const nextStep = organizationSuspended
    ? 'Contact your Route42 organization administrator or Route42 support to confirm the suspension and the steps required for reactivation. Suspension does not remove your Route42 identity or your access to other active organizations.'
    : membershipSuspended
      ? 'Contact an administrator for this organization to ask about restoring your membership. Your access to other active Route42 organizations is not affected.'
      : 'Contact your Route42 organization administrator or Route42 support to confirm the organization status and your access.';

  return (
    <main className="r42-organization-unavailable-page">
      <section className="r42-organization-unavailable-card" role="alert" aria-labelledby="organization-unavailable-title">
        <div className="r42-organization-unavailable-header">
          <Route42Logo variant="lockup" />
          <div className="r42-organization-unavailable-status">
            <i className="bi bi-exclamation-circle" aria-hidden="true" />
            <span>{organizationSuspended ? 'Organization suspended' : membershipSuspended ? 'Access suspended' : 'Organization unavailable'}</span>
          </div>
        </div>
        <span className="r42-eyebrow">Organization access</span>
        <h1 id="organization-unavailable-title">{title}</h1>
        <p>{explanation}</p>
        <div className="r42-organization-unavailable-guidance">
          <strong>What to do next</strong>
          <p>{nextStep}</p>
        </div>
        <div className="r42-organization-unavailable-actions">
          {otherOrganizations.length ? (
            <button className="btn r42-btn-primary" type="button" onClick={() => navigate('/organizations', { replace: true })}>
              Choose another organization
            </button>
          ) : null}
          <button className="btn btn-outline-secondary" type="button" onClick={() => void handleSignOut()}>Sign out</button>
        </div>
      </section>
    </main>
  );
}
